import { useRef, useState, type FormEvent } from 'react';
import { useImports, useUploadImport } from '@/lib/api/imports';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { QueryState } from '@/components/QueryState';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { ImportJobDetail } from '@/features/catalogs/ImportJobDetail';

interface ImportsPanelProps {
  catalogId: string;
}

/** Upload control, recent-imports list, and the active job's detail (AC-9). */
export function ImportsPanel({ catalogId }: ImportsPanelProps) {
  const imports = useImports(catalogId);
  const upload = useUploadImport(catalogId);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function handleUpload(e: FormEvent) {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;
    upload.mutate(file, {
      onSuccess: (job) => {
        setExpandedJobId(job.id);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  }

  return (
    <Card
      title="Import products"
      description="Upload a spreadsheet (.xlsx, .csv) or a PDF of product sheets."
    >
      <form onSubmit={handleUpload} className="mb-6 flex items-end gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="import-file-input">
            File
          </label>
          <input
            ref={fileInputRef}
            id="import-file-input"
            type="file"
            accept=".xlsx,.csv,.pdf"
            className="block w-full text-sm text-gray-700"
            data-testid="import-file-input"
          />
        </div>
        <Button type="submit" disabled={upload.isPending} data-testid="upload-import-button">
          {upload.isPending ? 'Uploading...' : 'Upload'}
        </Button>
      </form>

      {upload.error && (
        <p
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {(upload.error as Error).message}
        </p>
      )}

      <QueryState
        isLoading={imports.isLoading}
        error={imports.error}
        isEmpty={!imports.data || imports.data.items.length === 0}
        emptyMessage="No imports yet."
        loadingLabel="Loading imports"
        skeleton={<SkeletonRows rows={2} />}
      >
        <ul className="divide-y divide-gray-100" data-testid="import-job-list">
          {imports.data?.items.map((job) => (
            <li key={job.id} data-testid="import-job-item">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 py-2 text-left"
                onClick={() => setExpandedJobId(expandedJobId === job.id ? null : job.id)}
                data-testid="import-job-toggle"
              >
                <span className="truncate text-sm text-gray-900">{job.filename}</span>
                <span className="shrink-0 text-xs text-gray-500">{job.status}</span>
              </button>
              {expandedJobId === job.id && (
                <ImportJobDetail catalogId={catalogId} jobId={job.id} />
              )}
            </li>
          ))}
        </ul>
      </QueryState>
    </Card>
  );
}
